import {
    UsersIcon,
    AcademicCapIcon,
    SparklesIcon,
    HeartIcon,
} from "@heroicons/react/24/outline";
import React, {useEffect, useState} from "react";
import {useGuideFlowProviderContext} from "../../../../../GuideFlowProviderContext";
import {Stat, StatKey} from "../../../../../providers/stats/StatsProvider";
import {FullSpiritActivation, LifeActivation} from "../../../../../providers/definitions/builtin/BuiltInHealings";
import {EmpowerThyself} from "../../../../../providers/definitions/builtin/BuiltInClasses";
import { Button } from "../../../../controls/tailwindui/button";
import {Dialog, DialogActions, DialogBody, DialogTitle} from "../../../../controls/tailwindui/dialog";
import {UserIcon} from "@heroicons/react/20/solid";


export default function StatCardList(props: { lastXDays?: number }) {
    const [stats, setStats] = useState<Stat[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const context = useGuideFlowProviderContext();

    useEffect(() => {
        setLoaded(false);
        context.StatsProvider.getLiveStats(props.lastXDays).then((result) => {
            setStats(result);
            setLoaded(true);
        });
    }, [context.StatsProvider, context.StatsProvider.StateChanged, props.lastXDays]);

    if (!loaded) {
        return <div>Loading…</div>; // keep your skeleton loader if you prefer
    }

    const visibleStats = stats.slice(0, 3);
    const hasMore = stats.length > 3;

    return (
        <>
            {/* Inline preview (first 3) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleStats.map((stat) => (
                    <StatCard key={stat.definitionId} item={stat}/>
                ))}
            </div>

            {hasMore && (
                <div className="mt-3 flex justify-end">
                    <Button outline onClick={() => setShowDialog(true)}>
                        More…
                    </Button>
                </div>
            )}

            {/* Dialog with full list */}
            <Dialog
                size="4xl"
                open={showDialog}
                onClose={() => setShowDialog(false)}
                initialFocus={undefined}
            >
                <DialogTitle>
                    <div className="flex flex-row gap-3 justify-center">
                        <UserIcon className="h-6 w-6" aria-hidden="true"/>
                        <h3 className="text-base font-semibold leading-6 text-gray-900">
                            All Stats
                        </h3>
                    </div>
                </DialogTitle>

                <DialogBody className="text-sm/6 text-zinc-900 dark:text-white h-96 overflow-y-scroll border-b">
                    <div className="p-4 sm:p-6">
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(20rem,1fr))] gap-6">
                            {stats.map((stat) => (
                                <StatCard key={stat.definitionId} item={stat} />
                            ))}
                        </div>
                    </div>
                </DialogBody>


                <DialogActions className="w-full">
                    <Button
                        type="button"
                        onClick={() => setShowDialog(false)}
                        className="mt-1 inline-flex w-full justify-center"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}


function getIcon(definitionId: StatKey) {
    switch (definitionId) {
        case LifeActivation.id:
            return SparklesIcon;
        case FullSpiritActivation.id:
            return HeartIcon;
        case EmpowerThyself.id:
            return AcademicCapIcon;
        default:
            return UsersIcon;
    }
}

function StatCard(props: { item: Stat }) {
    const stat = props.item;
    if (!stat) {
        return <>Missing Stats</>;
    }

    const Icon = getIcon(stat.definitionId);

    // Build link depending on type
    let link: string | null = null;
    if (stat.definitionType === "healing") {
        link = `/healings/${stat.definitionId}/booking-history`;
    } else if (stat.definitionType === "class") {
        link = `/classes/${stat.definitionId}/booking-history`;
    }
    // if initiation → leave as null

    return (
        <div className="sm:min-h-40 lg:w-80 h-full">
            <div
                key={stat.definitionId}
                className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6"
            >
                <dt>
                    <div className="absolute rounded-md bg-indigo-500 p-3">
                        <Icon className="h-6 w-6 text-white" aria-hidden="true"/>
                    </div>
                    <p className="ml-16 truncate text-sm font-medium text-gray-500">
                        {stat.title}
                    </p>
                </dt>
                <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
                    <p className="text-2xl font-semibold text-gray-900">
                        {stat.totalEvents}
                    </p>
                </dd>

                {link && (
                    <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-3 sm:px-6">
                        <div className="text-sm">
                            <a
                                href={link}
                                className="font-medium text-indigo-600 hover:text-indigo-500"
                            >
                                View all →
                                <span className="sr-only"> {stat.title} stats</span>
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
